import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  useDetectBuilder,
  useConfirmBuilderConfig,
  useExtractAdapterConfig,
  useActivateAdapter,
  useInstallHelperPlugin,
} from '../hooks/useBuilderDetection';
import type { BuilderDetectionResult, DiscoveredAdapterConfig, TenantSettings } from '../types/tenant';

const { Text, Title } = Typography;

const KNOWN_BUILDERS = [
  { value: 'divi', label: 'Divi Builder' },
  { value: 'elementor', label: 'Elementor' },
  { value: 'avada', label: 'Avada (Fusion Builder)' },
  { value: 'flatsome', label: 'Flatsome (UX Builder)' },
  { value: 'wpbakery', label: 'WPBakery (Visual Composer)' },
  { value: 'gutenberg', label: 'Gutenberg (Block Editor)' },
  { value: 'oxygen', label: 'Oxygen Builder' },
  { value: 'beaver_builder', label: 'Beaver Builder' },
  { value: 'brizy', label: 'Brizy' },
];

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'green',
  medium: 'gold',
  low: 'red',
};

const STATUS_COLORS: Record<string, string> = {
  pending_review: 'gold',
  active: 'green',
  disabled: 'default',
};

const SOURCE_LABELS: Record<string, string> = {
  plugin: 'Active Plugin',
  theme: 'WordPress Theme',
  content: 'Content Analysis',
  manual: 'Manually Set',
  none: 'Default (Gutenberg)',
};

interface BuilderDetectionPanelProps {
  website: string | null;
  settings: TenantSettings | null | undefined;
}

const BuilderDetectionPanel: React.FC<BuilderDetectionPanelProps> = ({ website, settings }) => {
  const [detectionResult, setDetectionResult] = useState<BuilderDetectionResult | null>(null);
  const [manualBuilder, setManualBuilder] = useState<string | undefined>(undefined);

  const detectMutation = useDetectBuilder();
  const confirmMutation = useConfirmBuilderConfig();
  const extractMutation = useExtractAdapterConfig();
  const activateMutation = useActivateAdapter();
  const installPluginMutation = useInstallHelperPlugin();

  const handleDetect = () => {
    if (!website) return;
    detectMutation.mutate(website, {
      onSuccess: (result) => {
        setDetectionResult(result);
        setManualBuilder(result.detected_builder || undefined);
      },
    });
  };

  const handleConfirm = () => {
    if (!website) return;
    const builderToSave = manualBuilder || detectionResult?.detected_builder;
    if (!builderToSave) return;

    confirmMutation.mutate({
      website,
      config: {
        page_builder: builderToSave,
        seo_plugin: detectionResult?.detected_seo_plugin || undefined,
        theme: detectionResult?.detected_theme || undefined,
      },
    });
  };

  const handleExtract = () => {
    if (!website) return;
    extractMutation.mutate(website);
  };

  const handleActivate = (status: 'active' | 'disabled') => {
    if (!website) return;
    activateMutation.mutate({ website, status });
  };

  const hasWpCredentials = settings?.wp_user && settings?.wp_password;
  const currentBuilder = settings?.page_builder;
  const adapterConfig = settings?.builder_adapter_config as DiscoveredAdapterConfig | null | undefined;

  return (
    <div style={{ maxWidth: 700 }}>
      <Title level={5} style={{ marginBottom: 16 }}>Page Builder Detection</Title>

      {/* Current Config */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Current Builder">
            {currentBuilder ? (
              <Tag color="blue">{currentBuilder}</Tag>
            ) : (
              <Text type="secondary">Not configured</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Theme">
            {settings?.theme ? (
              <Tag>{settings.theme}</Tag>
            ) : (
              <Text type="secondary">Not set</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="SEO Plugin">
            {settings?.seo_plugin ? (
              <Tag>{settings.seo_plugin}</Tag>
            ) : (
              <Text type="secondary">Not set</Text>
            )}
          </Descriptions.Item>
          {adapterConfig && (
            <Descriptions.Item label="Adapter Config">
              <Space>
                <Tag color={STATUS_COLORS[adapterConfig.status]}>
                  {adapterConfig.status}
                </Tag>
                <Text type="secondary">
                  {adapterConfig.builder_name} ({adapterConfig.extraction_confidence} confidence)
                </Text>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Detection Action */}
      {!hasWpCredentials && (
        <Alert
          type="warning"
          message="WordPress credentials required"
          description="Configure wp_user and wp_password in the Integrations tab before running builder detection."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleDetect}
          loading={detectMutation.isPending}
          disabled={!hasWpCredentials || !website}
        >
          Detect Builder
        </Button>
        {detectionResult && (
          <Button
            icon={<ReloadOutlined />}
            onClick={handleDetect}
            loading={detectMutation.isPending}
          >
            Re-detect
          </Button>
        )}
      </Space>

      {/* Detection Results */}
      {detectMutation.isPending && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Spin size="small" /> <Text style={{ marginLeft: 8 }}>Querying WordPress site...</Text>
        </Card>
      )}

      {detectionResult && !detectMutation.isPending && (
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <Space>
              <Text strong>Detection Results</Text>
              <Tag color={CONFIDENCE_COLORS[detectionResult.confidence]}>
                {detectionResult.confidence} confidence
              </Tag>
            </Space>
          }
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Detected Builder">
              {detectionResult.detected_builder ? (
                <Space>
                  <Tag color={detectionResult.supported ? 'green' : 'orange'}>
                    {detectionResult.detected_builder}
                  </Tag>
                  {detectionResult.supported ? (
                    <Badge status="success" text="Adapter available" />
                  ) : (
                    <Badge status="warning" text="No adapter — extract config below" />
                  )}
                </Space>
              ) : (
                <Text type="secondary">None detected</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Detection Source">
              <Text>{SOURCE_LABELS[detectionResult.source] || detectionResult.source}</Text>
            </Descriptions.Item>
            {detectionResult.detected_theme && (
              <Descriptions.Item label="Theme">
                <Tag>{detectionResult.detected_theme}</Tag>
              </Descriptions.Item>
            )}
            {detectionResult.detected_seo_plugin && (
              <Descriptions.Item label="SEO Plugin">
                <Tag>{detectionResult.detected_seo_plugin}</Tag>
              </Descriptions.Item>
            )}
            {detectionResult.current_config && detectionResult.current_config.toLowerCase() !== detectionResult.detected_builder?.toLowerCase() && (
              <Descriptions.Item label="Config Mismatch">
                <Alert
                  type="warning"
                  message={`Current config "${detectionResult.current_config}" differs from detected "${detectionResult.detected_builder}"`}
                  banner
                />
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Manual Override + Confirm */}
          <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">Adjust if needed, then confirm to save:</Text>
              <Space>
                <Select
                  style={{ width: 250 }}
                  value={manualBuilder}
                  onChange={setManualBuilder}
                  options={KNOWN_BUILDERS}
                  placeholder="Select builder"
                />
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleConfirm}
                  loading={confirmMutation.isPending}
                  disabled={!manualBuilder}
                >
                  Confirm & Save
                </Button>
              </Space>
            </Space>
          </div>

          {/* Extract Adapter Config — always available after detection */}
          {detectionResult.detected_builder && (
            <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Adapter Config Extraction</Text>
                <Text type="secondary">
                  Extract shortcode patterns and meta keys from existing pages
                  for {detectionResult.detected_builder}.
                  {detectionResult.supported
                    ? ' Useful if the site has custom meta keys beyond the built-in adapter.'
                    : ' Required to enable publishing for this unsupported builder.'}
                </Text>
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={handleExtract}
                  loading={extractMutation.isPending}
                  disabled={!website}
                >
                  Extract Adapter Config
                </Button>
              </Space>
            </div>
          )}
        </Card>
      )}

      {/* Extraction Loading */}
      {extractMutation.isPending && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Spin size="small" /> <Text style={{ marginLeft: 8 }}>Extracting adapter config from existing pages...</Text>
        </Card>
      )}

      {/* Discovered Adapter Config */}
      {adapterConfig && (
        <AdapterConfigCard
          config={adapterConfig}
          onActivate={() => handleActivate('active')}
          onDisable={() => handleActivate('disabled')}
          activating={activateMutation.isPending}
          onInstallPlugin={() => {
            if (!website) return;
            installPluginMutation.mutate(website, {
              onSuccess: (data) => {
                if (data.installed && website) {
                  // Re-extract after successful install to pick up real meta keys
                  extractMutation.mutate(website);
                }
              },
            });
          }}
          installingPlugin={installPluginMutation.isPending || (installPluginMutation.isSuccess && extractMutation.isPending)}
          installResult={installPluginMutation.data ?? undefined}
        />
      )}

      {/* Error States */}
      {detectMutation.isError && (
        <Alert
          type="error"
          message="Detection Failed"
          description={detectMutation.error?.message || 'Unknown error'}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      {extractMutation.isError && (
        <Alert
          type="error"
          message="Extraction Failed"
          description={extractMutation.error?.message || 'Unknown error'}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
    </div>
  );
};

/** Sub-component showing the discovered adapter config with activate/disable controls. */
const AdapterConfigCard: React.FC<{
  config: DiscoveredAdapterConfig;
  onActivate: () => void;
  onDisable: () => void;
  activating: boolean;
  onInstallPlugin?: () => void;
  installingPlugin?: boolean;
  installResult?: { success: boolean; message: string; manual_install_required: boolean };
}> = ({ config, onActivate, onDisable, activating, onInstallPlugin, installingPlugin, installResult }) => {
  const metaKeyColumns = [
    { title: 'Meta Key', dataIndex: 'key', key: 'key' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  const metaKeyData = Object.entries(config.meta_keys || {}).map(([key, value]) => ({
    key,
    value: String(value),
  }));

  const hasUsefulData = config.shortcode_wrapper || metaKeyData.length > 0;

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <Text strong>Discovered Adapter Config</Text>
          <Tag color={STATUS_COLORS[config.status]}>{config.status}</Tag>
          <Tag color={CONFIDENCE_COLORS[config.extraction_confidence]}>
            {config.extraction_confidence} confidence
          </Tag>
        </Space>
      }
    >
      {/* No content found — show guidance */}
      {config.no_content && !config.referenced_from && (
        <Alert
          type="info"
          message="No pages or posts found on this site"
          description="Create at least one page using the builder's visual editor in wp-admin, then re-run extraction to discover shortcode patterns and meta keys."
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {/* Referenced from another tenant */}
      {config.referenced_from && (
        <Alert
          type="info"
          message={`Config referenced from ${config.referenced_from}`}
          description={`No content found on this site, but another tenant using ${config.builder_name} already has an extracted config. The same shortcode patterns and meta keys apply across all ${config.builder_name} sites.`}
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {/* Known builder hints — offer plugin install for real extraction */}
      {config.meta_source === 'known_builder_hints' && (
        <Alert
          type="info"
          message="Meta keys from known builder patterns"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>
                {config.hints_notes || `${config.builder_name}'s data isn't exposed via the REST API. These meta keys are based on known patterns.`}
              </Text>
              <Text>
                Install the helper plugin on the WordPress site to enable real extraction of meta keys and values.
              </Text>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CloudUploadOutlined />}
                  onClick={onInstallPlugin}
                  loading={installingPlugin}
                  disabled={!onInstallPlugin}
                >
                  {installingPlugin ? 'Installing & Re-extracting...' : 'Install Helper Plugin'}
                </Button>
              </Space>
              {installResult && !installResult.success && installResult.manual_install_required && (
                <Alert
                  type="warning"
                  message="Auto-install failed"
                  description={
                    <>
                      <Text>{installResult.message}</Text>
                      <br />
                      <Text type="secondary">
                        Manual install: Copy <Text code>agileco-rest-meta-exposer.php</Text> to{' '}
                        <Text code>wp-content/mu-plugins/</Text> on the WordPress site, then re-run extraction.
                      </Text>
                    </>
                  }
                  style={{ marginTop: 8 }}
                  showIcon
                />
              )}
            </Space>
          }
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {/* Manually seeded config */}
      {config.meta_source === 'manual_seed' && (
        <Alert
          type="info"
          message="Manually seeded config"
          description="This adapter config was manually provided, not auto-extracted from existing pages."
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      <Descriptions column={1} size="small">
        <Descriptions.Item label="Builder">
          <Tag>{config.builder_name}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Discovered">
          <Text type="secondary">{new Date(config.discovered_at).toLocaleString()}</Text>
        </Descriptions.Item>
        {config.shortcode_wrapper && (
          <Descriptions.Item label="Shortcode Pattern">
            <Text code>
              {config.shortcode_wrapper.container_open}...{config.shortcode_wrapper.text_open}
              [content]{config.shortcode_wrapper.text_close}...{config.shortcode_wrapper.container_close}
            </Text>
          </Descriptions.Item>
        )}
        {!config.shortcode_wrapper && !config.no_content && (
          <Descriptions.Item label="Shortcode Pattern">
            <Text type="secondary">None discovered (raw HTML will be used)</Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {metaKeyData.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Builder Meta Keys</Text>
          <Table
            columns={metaKeyColumns}
            dataSource={metaKeyData}
            size="small"
            pagination={false}
          />
        </div>
      )}

      {config.sample_pages && config.sample_pages.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">
            Analyzed {config.sample_pages.length} page(s): {config.sample_pages.map(p => p.title || p.page_id).join(', ')}
          </Text>
        </div>
      )}

      <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        <Space>
          {config.status !== 'active' && hasUsefulData && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={onActivate}
              loading={activating}
            >
              Activate Adapter
            </Button>
          )}
          {config.status === 'active' && (
            <Button
              icon={<StopOutlined />}
              onClick={onDisable}
              loading={activating}
              danger
            >
              Disable Adapter
            </Button>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default BuilderDetectionPanel;
